'use client'

import type { FormAction } from '@/core/action'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/navigation'
import { useActionState, useLayoutEffect, useMemo } from 'react'
import { SubmitButton } from '../components/submit-button'
import { useToast } from '../components/use-toast'
import { AutoForm, type AutoFormSchema, Form } from '../form'

type UpdatePasswordForm = {
  action: FormAction<boolean>
}

export const UpdatePasswordForm = ({ action }: UpdatePasswordForm) => {
  const t = useTranslations('pages.updatePassword.form')
  const router = useRouter()
  const { toast } = useToast()

  const [state, formAction] = useActionState(action, null)

  useLayoutEffect(() => {
    if (state?.success) {
      router.push('/')
      toast({
        variant: 'default',
        title: t('toasts.success.title'),
        description: t('toasts.success.description'),
      })
    }

    if (state?.errors.length) {
      toast({
        variant: 'destructive',
        title: t('toasts.error.title'),
        description: t('toasts.error.description'),
      })
    }
  }, [t, toast, state, router])

  const schema: AutoFormSchema = useMemo(
    () => [
      {
        path: 'password',
        component: 'password',
        classNames: { root: 'mb-4' },
        options: {
          label: t('fields.password.label'),
        },
      },
      {
        path: 'confirmPassword',
        component: 'password',
        classNames: { root: 'mb-4' },
        options: {
          label: t('fields.confirmPassword.label'),
        },
      },
    ],
    [t],
  )

  return (
    <Form state={state} action={formAction}>
      <AutoForm schema={schema} />
      <SubmitButton
        className="w-full mt-6"
        loadingText={t('actions.loading')}
        actionText={t('actions.send')}
      />
    </Form>
  )
}
